import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import FlagDisplay from './FlagDisplay';

const TranslationInterface = ({
  languages,
  selectedLanguage,
  setSelectedLanguage,
  inputText,
  setInputText,
  translationResult,
  genderFilter,
  setGenderFilter,
  isLoading,
  onTranslate,
  onClear,
  showNotification,
  isDarkMode,
  appSettings = { audioGloballyEnabled: true, voiceInputEnabled: true, feedbackEnabled: true },
  API,
  setCurrentView,
  setInitialProfileTab,
  userFeatures = null,
  userToken = null,
  hasPaidPlan,
  translationLimitInfo = { canTranslate: true, remaining: -1, limit: 5, used: 0, plan: "Free Plan" },
  checkTranslationLimit = () => {},
  isFavoritesOpen = false,
  setIsFavoritesOpen = () => {},
  isCategoriesOpen = false,
  setIsCategoriesOpen = () => {},
  isSuggestPopupOpen = false,
  setIsSuggestPopupOpen = () => {}
}) => {
  const { t } = useTranslation();

  // Admin-managed page labels. Single source of truth =
  // backend/routes/translation_page_settings_routes.py → DEFAULT_LABELS.
  // Local fallback duplicates the backend defaults so labels render sensibly
  // during the brief fetch window before the API responds (and if the API
  // ever fails completely, the page still has text). Once the fetch resolves,
  // admin's values overlay these defaults.
  const _DEFAULT_PAGE_LABELS = useMemo(() => ({
    daily_limit_reached_title: 'Daily Limit Reached',
    upgrade_cta_guest_text: 'Create a free account to continue learning, or upgrade for unlimited translations!',
    upgrade_cta_guest_button: '🚀 Create Account or Upgrade',
    guest_come_back_info: '⏰ Come back tomorrow at midnight for more free translations',
    upgrade_cta_pro_text: 'Upgrade to a Pro membership for unlimited translations and premium features!',
    upgrade_cta_pro_button: '⭐ Upgrade to Pro',
    come_back_tomorrow_button: 'Come Back Tomorrow',
    limit_reset_info: '⏰ Your limit resets at midnight',
    voice_locked_guest: '🔒 Login to use voice input',
    voice_locked_user: '🔒 Upgrade to Pro for voice input',
    voice_locked_notification: 'Upgrade to Pro for voice input',
    favorites_locked_guest: '🔒 Login to use favorites',
    favorites_locked_user: '🔒 Upgrade to Pro for favorites',
    favorites_locked_notification: 'Upgrade to Pro to save favorites',
    audio_locked_guest: '🔒 Login to use audio',
    audio_locked_user: '🔒 Upgrade to Pro for audio',
    audio_locked_notification: 'Upgrade to Pro for audio',
    word_bank_locked: 'Upgrade to Pro to save words to your Word Bank',
    added_to_favorites: 'Added to favorites ✓',
    removed_from_favorites: 'Removed from favorites ✓',
    add_to_favorites_tooltip: 'Add to favorites',
    audio_not_supported: 'Audio not supported in this browser',
    audio_playback_failed: 'Audio playback failed',
    copy_success: 'Copied to clipboard!',
    copy_failed: 'Failed to copy text',
    todays_usage_label: "Today's Usage",
    current_plan_label: 'Current plan:',
    guest_empty_state_hint: 'Sign up for a free account or upgrade for unlimited translations',
  }), []);
  const [pageLabels, setPageLabels] = useState(_DEFAULT_PAGE_LABELS);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/translation/page-settings`);
        if (!r.ok) return;
        const d = await r.json();
        if (!alive || !d) return;
        if (d.labels) setPageLabels({ ..._DEFAULT_PAGE_LABELS, ...d.labels });
        // Translation owns its title: read the admin-set page_title and
        // write it directly to the document title once the settings API
        // resolves. The static index.html title (Darija Pro brand fallback)
        // shows for ~50ms before this fires — recognizable as the brand,
        // never as the landing redirect.
        if (d.page_title) {
          try { document.title = d.page_title; } catch (e) {}
          // Write to the pageMetaCache the inline pre-render script in
          // index.html (lines ~76-82) reads on the NEXT page load. After the
          // first visit, every subsequent refresh on this device sets the
          // title from this cache BEFORE React mounts — kills the ~50ms
          // brand-flash on cold reload.
          try {
            const cache = JSON.parse(localStorage.getItem('pageMetaCache') || '{}');
            cache['/translation'] = d.page_title;
            localStorage.setItem('pageMetaCache', JSON.stringify(cache));
          } catch (e) {}
        }
      } catch (e) { /* keep defaults */ }
    })();
    return () => { alive = false; };
  }, [API, _DEFAULT_PAGE_LABELS]);

const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [showMicHelpModal, setShowMicHelpModal] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);
  const [userFeedback, setUserFeedback] = useState(null); // 'like' or 'dislike' or null
  const [isLearning, setIsLearning] = useState(false);
  const [translationWordsToday, setTranslationWordsToday] = useState({ count: 0, limit: 5, nextResetTime: null });
  const [freeTranslationsUsed, setFreeTranslationsUsed] = useState(0);
  const [sessionTranslationCount, setSessionTranslationCount] = useState(0); // Track how many translations APPEARED on screen
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false); // Track if limit is reached
  const FREE_TRANSLATION_LIMIT = appSettings?.freeTranslationsPerDay || 5;
  const recognitionRef = React.useRef(null);
  const [showAllVariants, setShowAllVariants] = useState(false);
  const [scriptView, setScriptView] = useState('latin'); // 'latin' | 'arabic'
  const [disambiguationSelected, setDisambiguationSelected] = useState(null); // Selected disambiguation entry
  const [disambiguationLoading, setDisambiguationLoading] = useState(false);

  // Autocomplete suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const suggestionTimerRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Smart gender display: swap primary with matching variant if gender filter is active
  // Also: always prefer masculine non-plural as primary for verb conjugations
  // Also: use disambiguation selection if available
  const displayResult = React.useMemo(() => {
    // If disambiguation was selected, use that result
    if (disambiguationSelected) {
      return {
        ...translationResult,
        translatedText: disambiguationSelected.translatedText,
        latinScript: disambiguationSelected.latinScript,
        gender: disambiguationSelected.gender,
        number: disambiguationSelected.number,
        audioUrl: disambiguationSelected.audioUrl,
        status: disambiguationSelected.status,
        note: disambiguationSelected.note,
        specialStyle: disambiguationSelected.specialStyle,
        disambiguation: false,
        disambiguationOptions: null,
        variants: null
      };
    }
    if (!translationResult) return translationResult;

    // Helper to swap a variant into primary position
    const swapToPrimary = (result, variant) => {
      const originalPrimary = {
        id: result.id,
        translatedText: result.translatedText,
        latinScript: result.latinScript,
        gender: result.gender,
        number: result.number,
        audioUrl: result.audioUrl,
        isPrimary: result.isPrimary,
        note: result.note
      };
      const newVariants = result.variants
        .filter(v => v !== variant)
        .concat([originalPrimary]);
      return {
        ...result,
        translatedText: variant.translatedText,
        latinScript: variant.latinScript,
        gender: variant.gender,
        number: variant.number,
        audioUrl: variant.audioUrl,
        note: variant.note ?? result.note,
        variants: newVariants
      };
    };

    // Gender filter swap: if user selected a gender filter, show matching variant as primary
    if (genderFilter && translationResult.variants) {
      if (translationResult.gender !== genderFilter) {
        const matchingVariant = translationResult.variants.find(v => v.gender === genderFilter);
        if (matchingVariant) return swapToPrimary(translationResult, matchingVariant);
      }
      return translationResult;
    }

    // No gender filter: prefer masculine non-plural as primary for verb conjugations
    // Triggers when variants exist and primary is not already masculine non-plural
    if (translationResult.variants && translationResult.variants.length > 0) {
      const isMaleNonPlural = translationResult.gender === 'male' && translationResult.number !== 'plural';
      if (!isMaleNonPlural) {
        const maleVariant = translationResult.variants.find(v => v.gender === 'male' && v.number !== 'plural');
        if (maleVariant) return swapToPrimary(translationResult, maleVariant);
      }
    }

    return translationResult;
  }, [translationResult, genderFilter, disambiguationSelected]);


  // Party particle dots — positions and colors only (animation handled by CSS nth-child)
  const partyDots = React.useMemo(() => {
    const colors = ['#ff4444','#ff7733','#ffdd33','#44ff44','#33ddff','#4477ff','#9944ff','#ff44cc',
                    '#ff6666','#ffaa44','#ffff44','#66ff66','#44ffdd','#6699ff','#bb66ff','#ff66dd',
                    '#ff3355','#ff8800','#aaff33','#33ffaa','#3399ff','#aa44ff','#ff33aa','#ffcc00'];
    return Array.from({length: 24}, (_, i) => {
      const angle = (i / 24) * Math.PI * 2;
      const startR = 10 + (i % 4) * 5;
      return {
        style: {
          left: `calc(50% + ${Math.round(Math.cos(angle) * startR)}px)`,
          top: `calc(50% + ${Math.round(Math.sin(angle) * startR)}px)`,
          width: `${3 + (i % 3) * 2}px`,
          height: `${3 + (i % 3) * 2}px`,
          backgroundColor: colors[i],
        }
      };
    });
  }, []);

  // Confetti pieces — gold & green rectangles (animation handled by CSS nth-child)
  const confettiPieces = React.useMemo(() => {
    const colors = ['#ffc600', '#159b36'];
    return Array.from({length: 30}, (_, i) => ({
      style: {
        left: `${(i / 30) * 100}%`,
        top: `${15 + (i % 5) * 15}%`,
        width: `${6 + (i % 3)}px`,
        height: `${3 + (i % 2)}px`,
        backgroundColor: colors[i % 2],
      }
    }));
  }, []);

  // Voltage dots — electric sparks flying up/down (animation handled by CSS nth-child)
  const voltageDots = React.useMemo(() => {
    const colors = ['#6bfeff','#f6de8d','#5978F3','#6bfeff','#f6de8d','#5978F3','#6bfeff','#f6de8d',
                    '#5978F3','#6bfeff','#f6de8d','#5978F3','#6bfeff','#f6de8d','#5978F3','#6bfeff'];
    return Array.from({length: 16}, (_, i) => ({
      style: {
        left: `${8 + (i / 16) * 84}%`,
        top: i < 8 ? '5%' : '95%',
        width: '8px',
        height: '8px',
        backgroundColor: colors[i],
        color: colors[i],
      }
    }));
  }, []);

  // Reset showAllVariants and disambiguation when translation changes
  useEffect(() => {
    setShowAllVariants(false);
    setDisambiguationSelected(null);
  }, [translationResult?.translatedText]);

  // Handle disambiguation selection — call /translate/select to get specific translation
  const handleDisambiguationSelect = async (option) => {
    setDisambiguationLoading(true);
    try {
      const res = await fetch(`${API}/translate/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: option.id })
      });
      if (res.ok) {
        const data = await res.json();
        setDisambiguationSelected({ ...data, id: option.id, selectedNote: option.note });
      }
    } catch (err) {
      console.error('Disambiguation select error:', err);
    }
    setDisambiguationLoading(false);
  };
  
  // Track when translation result appears and increment counter
  useEffect(() => {
    // Skip limit check for paid users
    if (hasPaidPlan) {
      return; // Paid users have unlimited translations
    }
    
    // Only count when a successful translation appears (has translatedText)
    if (translationResult?.translatedText && !translationResult?.limitExceeded && !translationResult?.notFound) {
      setSessionTranslationCount(prev => {
        const newCount = prev + 1;
        console.log(`Translation #${newCount} appeared: ${translationResult.translatedText}`);
        
        // Check if we hit the limit (only for free users)
        if (newCount >= FREE_TRANSLATION_LIMIT) {
          console.log(`Limit reached! Locking screen after ${newCount} translations`);
          setIsLimitReached(true);
          setShowLimitModal(true);
        }
        
        return newCount;
      });
    }
  }, [translationResult, FREE_TRANSLATION_LIMIT, hasPaidPlan]);
  
  const selectedLang = languages.find(lang => lang.code === selectedLanguage);

  // Load favorites on mount and when user logs in
  useEffect(() => {
    loadFavorites();
    
    // Load free translation count from localStorage (for non-logged users)
    if (!userToken) {
      const stored = localStorage.getItem('freeTranslationsUsed');
      if (stored) {
        setFreeTranslationsUsed(parseInt(stored));
      }
    } else {
      // Reset for logged-in users
      setFreeTranslationsUsed(0);
      localStorage.removeItem('freeTranslationsUsed');
    }
  }, [API, userToken]); // Add userToken as dependency
  
  // Re-translate when gender filter changes (if there's existing input)
  useEffect(() => {
    if (inputText && inputText.trim() && selectedLanguage) {
      console.log('[GENDER CHANGE] Re-translating with new gender filter:', genderFilter);
      onTranslate(inputText, selectedLanguage);
    }
  }, [genderFilter]); // Only watch genderFilter
  
  // Show limit modal when translation limit is exceeded
  useEffect(() => {
    if (translationResult?.limitExceeded) {
      setShowLimitModal(true);
      setIsLimitReached(true); // Lock the screen
      // Clear input so user cannot see their text
      setInputText('');
    }
  }, [translationResult, setInputText]);
  
  // Also check translationLimitInfo to prevent typing before limit is hit
  useEffect(() => {
    // Skip limit check for paid users
    if (hasPaidPlan) {
      return; // Paid users have unlimited translations
    }
    
    if (translationLimitInfo && !translationLimitInfo.canTranslate && translationLimitInfo.remaining === 0) {
      setIsLimitReached(true);
      setShowLimitModal(true);
    }
  }, [translationLimitInfo, hasPaidPlan]);

  const loadFavorites = async () => {
    // Only load if user is logged in
    if (!userToken) {
      setFavorites([]);
      return;
    }
    
    try {
      const response = await fetch(`${API}/favorites`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      } else if (response.status === 401 || response.status === 404) {
        // Token expired or invalid
        setFavorites([]);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
      setFavorites([]);
    }
  };

  const addToFavorites = async () => {
    if (!translationResult || !inputText) return;
    
    // Check if user is logged in
    if (!userToken) {
      showNotification('Please login to save favorites', 'info');
      return;
    }
    
    // Check if already favorited
    if (isFavorited) {
      showNotification('Already in favorites!', 'info');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('sourceText', inputText);
      formData.append('translatedText', translationResult.translatedText);
      formData.append('sourceLang', selectedLanguage);
      formData.append('audioUrl', translationResult.audioUrl || '');

      const response = await fetch(`${API}/favorites`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${userToken}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        // Immediately add to local state for instant UI update
        const newFavorite = {
          id: data.id,
          sourceText: inputText,
          translatedText: translationResult.translatedText,
          sourceLang: selectedLanguage,
          audioUrl: translationResult.audioUrl || '',
          createdAt: new Date().toISOString()
        };
        
        setFavorites(prevFavorites => [newFavorite, ...prevFavorites]);
        setIsFavorited(true);
        showNotification(`✅ Added "${inputText}" to favorites!`, 'success');
        
        // Also reload from server to ensure sync
        loadFavorites();
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to add to favorites', 'error');
      }
    } catch (error) {
      console.error('Error adding to favorites:', error);
      showNotification('Failed to add to favorites', 'error');
    }
  };

  const removeFromFavorites = async (favoriteId) => {
    // Check if user is logged in
    if (!userToken) {
      showNotification('Please login to manage favorites', 'info');
      return;
    }
    
    try {
      const response = await fetch(`${API}/favorites/${favoriteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });

      if (response.ok) {
        // Immediately update local state by filtering out the deleted item
        setFavorites(prevFavorites => prevFavorites.filter(f => f.id !== favoriteId));
        showNotification(pageLabels.removed_from_favorites, 'success');
        
        // Also reload from server to ensure sync
        await loadFavorites();
        
        // Check if current translation is still favorited
        if (translationResult && inputText) {
          const stillFavorited = favorites.some(
            f => f.id !== favoriteId && f.sourceText === inputText && f.sourceLang === selectedLanguage
          );
          setIsFavorited(stillFavorited);
        }
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to remove from favorites', 'error');
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
      showNotification('Failed to remove from favorites', 'error');
    }
  };

  const submitFeedback = async (feedbackType) => {
    if (!translationResult) return;

    try {
      const formData = new FormData();
      formData.append('sourceText', inputText);
      formData.append('translatedText', translationResult.translatedText);
      formData.append('sourceLang', selectedLanguage);
      formData.append('feedbackType', feedbackType);

      const response = await fetch(`${API}/feedback`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setUserFeedback(feedbackType);
        showNotification(`Thank you for your ${feedbackType === 'like' ? '👍' : '👎'} feedback!`, 'success');
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to submit feedback', 'error');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showNotification('Failed to submit feedback', 'error');
    }
  };

  const markAsLearned = async () => {
    if (!translationResult || !inputText) return;
    
    // Check if user is logged in
    if (!userToken) {
      showNotification('Please login to add words to learning', 'info');
      return;
    }

    // Check daily translation limit (5 words/day for Progress Tracking)
    try {
      const progressResponse = await fetch(`${API}/user/translation-progress/today`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      
      if (progressResponse.ok) {
        const progress = await progressResponse.json();
        setTranslationWordsToday(progress);
        
        if (progress.limit_reached) {
          showNotification('🎉 Wow! You finished 5 words for today! Come back tomorrow for more.', 'success');
          return;
        }
      }
    } catch (error) {
      console.error('Error checking translation limit:', error);
    }

    try {
      const formData = new FormData();
      formData.append('sourceText', inputText);
      formData.append('translatedText', translationResult.translatedText);
      formData.append('latinScript', translationResult.latinScript || '');
      formData.append('sourceLang', selectedLanguage);
      formData.append('audioUrl', translationResult.audioUrl || '');
      formData.append('token', userToken);
      formData.append('source', 'translation'); // Track source as translation

      const response = await fetch(`${API}/learning/mark-learned`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (data.message && data.message.includes('Already')) {
          showNotification('Already saved in your Word Bank!', 'info');
        } else {
          setIsLearning(true);

          // Check if they just hit the limit
          if (data.today_translation >= 5) {
            showNotification('🎉 Wow! You saved 5 words for today! Come back tomorrow for more.', 'success');
            setTranslationWordsToday({ count: 5, limit: 5, remaining: 0, limit_reached: true });
          } else {
            showNotification(`📚 Saved to Word Bank! (${data.today_translation}/5 today)`, 'success');
            setTranslationWordsToday({ count: data.today_translation, limit: 5, remaining: 5 - data.today_translation, limit_reached: false });
          }
          
        }
      } else if (response.status === 401 || response.status === 404) {
        showNotification('Please login to add words to learning', 'info');
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to add to learning', 'error');
      }
    } catch (error) {
      console.error('Error marking as learned:', error);
      showNotification('Failed to add to learning', 'error');
    }
  };

  // Check if current translation is favorited
  useEffect(() => {
    if (translationResult && inputText) {
      const found = favorites.find(
        f => f.sourceText === inputText && f.sourceLang === selectedLanguage
      );
      setIsFavorited(!!found);
      // Reset feedback when translation changes
      setUserFeedback(null);
    } else {
      setIsFavorited(false);
      setUserFeedback(null);
    }
  }, [translationResult, inputText, selectedLanguage, favorites]);

  // Initialize Speech Recognition
  useEffect(() => {
    if (appSettings.voiceInputEnabled && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      
      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputText(transcript);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'no-speech') return;
        // Map browser error codes to user-friendly messages.
        let msg;
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          msg = t('voice_error_not_allowed');
        } else if (event.error === 'audio-capture') {
          msg = t('voice_error_no_mic');
        } else if (event.error === 'network') {
          msg = t('voice_error_network');
        } else {
          msg = t('voice_error_generic');
        }
        showNotification(msg, "error");
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [appSettings.voiceInputEnabled, setInputText, showNotification]);

  const toggleVoiceInput = async () => {
    if (!recognitionRef.current) {
      showNotification(t('voice_error_not_supported'), "warning");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      return;
    }

    // Layer 1 — proactively request mic permission before starting recognition.
    // First-time users see the browser's native permission popup (cleanest UX).
    // Already-denied users get our help modal (Layer 2) with browser-specific steps.
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // We only needed the prompt — release the stream immediately.
        stream.getTracks().forEach(track => track.stop());
      } catch (e) {
        if (e && (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError')) {
          setShowMicHelpModal(true);
          return;
        }
        if (e && (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError')) {
          showNotification(t('voice_error_no_mic'), "error");
          return;
        }
        showNotification(t('voice_error_generic'), "error");
        return;
      }
    }

    recognitionRef.current.start();
    setIsListening(true);
    showNotification(t('voice_listening'), "info");
  };

  // Reset Save button when input changes (new word = new save state)
  useEffect(() => {
    setIsLearning(false);
  }, [inputText]);

  // Fast Auto-translate: Works with any text length (even 1-2 characters for short Darija words)
  useEffect(() => {
    const trimmedText = inputText.trim();

    if (trimmedText.length >= 1 && !isLimitReached) {
      const delayedTranslate = setTimeout(() => {
        onTranslate(inputText, selectedLanguage);
      }, 1000);  // 1 second delay
      return () => clearTimeout(delayedTranslate);
    }
  }, [inputText, selectedLanguage, onTranslate, isLimitReached]);

  // Autocomplete suggestions — 300ms debounce, prefix search from DB
  useEffect(() => {
    if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current);
    const trimmed = inputText.trim();
    if (trimmed.length >= 2 && !isLimitReached) {
      suggestionTimerRef.current = setTimeout(async () => {
        try {
          const res = await fetch(
            `${API}/suggestions?q=${encodeURIComponent(trimmed)}&lang=${selectedLanguage}`
          );
          if (res.ok) {
            const data = await res.json();
            const list = data.suggestions || [];
            setSuggestions(list);
            setShowSuggestions(list.length > 0);
            setSelectedSuggestionIndex(-1);
          }
        } catch (err) {
          // Suggestions are non-critical, silently fail
        }
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    return () => { if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current); };
  }, [inputText, selectedLanguage, isLimitReached, API]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSuggestionClick = useCallback((suggestion) => {
    setInputText(suggestion);
    setSuggestions([]);
    setShowSuggestions(false);
    onTranslate(suggestion, selectedLanguage);
  }, [setInputText, onTranslate, selectedLanguage]);

  const handleSuggestionKeyDown = useCallback((e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionClick(suggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }, [showSuggestions, suggestions, selectedSuggestionIndex, handleSuggestionClick]);

  // Reference to current audio instance
  const audioRef = React.useRef(null);

  // Audio playback with URL or TTS fallback
  const playAudio = useCallback(async (text, audioUrl = null) => {
    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      
      // Stop any TTS
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }

      // First try to play audio URL from backend if provided
      if (audioUrl && audioUrl.trim()) {
        const audio = new Audio(audioUrl);
        audio.loop = false; // Ensure it doesn't loop
        audioRef.current = audio;
        
        audio.play().catch(err => {
          console.error('Audio URL playback failed, using TTS:', err);
          audioRef.current = null;
          // Fallback to TTS if URL fails
          playTTS(text);
        });
        
        // Clean up when finished
        audio.onended = () => {
          audioRef.current = null;
        };
      } else {
        // Use TTS if no audio URL
        playTTS(text);
      }
    } catch (error) {
      console.error('Audio error:', error);
      showNotification(pageLabels.audio_playback_failed, "error");
    }
  }, [showNotification]);

  const playTTS = (text) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'ar-MA'; // Moroccan Arabic
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    } else {
      showNotification(pageLabels.audio_not_supported, "warning");
    }
  };

  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(t('copied_to_clipboard'), "success");
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        showNotification(pageLabels.copy_success, "success");
      } catch (fallbackError) {
        showNotification(pageLabels.copy_failed, "error");
      }
      document.body.removeChild(textArea);
    }
  }, [showNotification]);

  return (
    <div className="space-y-4">
      {/* Translation Limit Exceeded Modal - Full Screen Lock */}
      {showLimitModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0, 0, 0, 0.75)' }}>
          <div className="max-w-md w-full rounded-3xl shadow-2xl p-8 transform transition-all animate-fadeIn bg-gradient-to-br from-white via-blue-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 border-2 border-white/20 dark:border-gray-700/50">
            {/* Close Button */}
            <button
              onClick={() => {
                setShowLimitModal(false);
                setIsLimitReached(false);
              }}
              className="absolute top-4 right-4 p-2 rounded-full transition-all hover:bg-white/50 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:rotate-90"
              style={{ transition: 'all 0.3s ease' }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Animated Icon */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 via-red-500 to-pink-500 mb-5 shadow-2xl animate-pulse">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              
              <h3 className="text-3xl font-bold mb-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent">
                {pageLabels.daily_limit_reached_title}
              </h3>
              
              <p className="text-base mb-4 text-gray-700 dark:text-gray-300 font-medium">
                You've used all your free translations for today
              </p>
            </div>

            {/* Usage Stats with Gradient */}
            {translationResult?.limitExceeded && (
              <div className="p-5 rounded-2xl mb-6 bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-800/50 dark:to-gray-700/50 border border-orange-200 dark:border-gray-600 shadow-inner">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-gray-800 dark:text-gray-200">
                    {pageLabels.todays_usage_label}
                  </span>
                  <span className="text-lg font-extrabold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                    {translationResult.used} / {translationResult.limit}
                  </span>
                </div>
                <div className="h-3 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-600 shadow-inner">
                  <div 
                    className="h-full bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 transition-all duration-500 shadow-lg"
                    style={{ width: `${(translationResult.used / translationResult.limit) * 100}%` }}
                  />
                </div>
                <p className="text-xs mt-3 text-gray-600 dark:text-gray-400 font-medium">
                  {pageLabels.current_plan_label} <span className="font-bold text-gray-800 dark:text-gray-200">{translationResult.currentPlan === "Guest" ? "Free Plan" : translationResult.currentPlan}</span>
                </p>
              </div>
            )}

            {/* Call to Action - Different for Guest vs Logged Users */}
            {!userToken ? (
              <div className="space-y-3">
                <p className="text-sm text-center mb-4 text-gray-700 dark:text-gray-300 font-medium">
                  {pageLabels.upgrade_cta_guest_text}
                </p>
                <button
                  onClick={() => {
                    setShowLimitModal(false);
                    setIsLimitReached(false);
                    setCurrentView('profile');
                  }}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white rounded-2xl hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 font-bold text-lg transition-all shadow-xl hover:shadow-2xl transform hover:scale-105"
                  style={{ transition: 'all 0.3s ease' }}
                >
                  {pageLabels.upgrade_cta_guest_button}
                </button>
                <p className="text-xs text-center text-gray-600 dark:text-gray-400 font-medium">
                  {pageLabels.guest_come_back_info}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-center mb-4 text-gray-700 dark:text-gray-300 font-medium">
                  {pageLabels.upgrade_cta_pro_text}
                </p>
                <button
                  onClick={() => {
                    setShowLimitModal(false);
                    setIsLimitReached(false);
                    setInitialProfileTab('membership');
                    setCurrentView('profile');
                  }}
                  className="w-full py-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white rounded-2xl hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 font-bold text-lg transition-all shadow-xl hover:shadow-2xl transform hover:scale-105"
                  style={{ transition: 'all 0.3s ease' }}
                >
                  {pageLabels.upgrade_cta_pro_button}
                </button>
                <button
                  onClick={() => {
                    setShowLimitModal(false);
                    setIsLimitReached(false);
                  }}
                  className="w-full py-3 rounded-2xl font-semibold transition-all bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
                >
                  {pageLabels.come_back_tomorrow_button}
                </button>
                <p className="text-xs text-center text-gray-600 dark:text-gray-400 font-medium">
                  {pageLabels.limit_reset_info}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Translator Interface */}
      {/* Card wrapper - border/shadow/bg only on desktop, transparent on mobile */}
      <div className={`lg:rounded-3xl lg:shadow-2xl lg:border-2 transition-all duration-300 lg:p-6 ${
        isDarkMode 
          ? 'lg:bg-gradient-to-br lg:from-gray-800 lg:via-gray-900 lg:to-gray-800 lg:border-gray-700' 
          : 'lg:bg-gradient-to-br lg:from-white lg:via-blue-50 lg:to-white lg:border-blue-100'
      }`}>
      
      {/* Language Header Bar - Desktop Only */}
      {/* Desktop: Combined layout - Header row + Textareas in same grid for perfect alignment */}
      {/* `relative` on the grid makes it the positioning context for the Pro paywall overlay below. */}
      <div className="hidden lg:grid lg:grid-cols-2 gap-4 md:gap-6 relative">
        {/* LEFT COLUMN: Source Language dropdown + Gender + Input textarea */}
        <div className="flex flex-col">
          {/* Source Language Dropdown + Gender Selector */}
          <div className="flex items-center gap-4 mb-4">
            {/* Language Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all duration-300 font-semibold shadow-md hover:shadow-lg ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                    : 'bg-white border-blue-200 hover:bg-blue-50 hover:border-blue-300'
                }`}
              >
                <FlagDisplay code={selectedLang?.code} fallbackText={selectedLang?.code} />
                <span className="font-semibold">{selectedLang?.name || "English"}</span>
                <svg className={`w-4 h-4 transition-transform duration-300 ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isLanguageDropdownOpen && (
                <div className={`absolute top-full left-0 mt-2 w-56 rounded-2xl shadow-2xl max-h-64 overflow-y-auto z-50 border-2 ${
                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-200'
                }`}>
                  <div className="py-2">
                    {languages.map((language) => (
                      <button
                        key={language.id}
                        onClick={() => {
                          setSelectedLanguage(language.code);
                          setIsLanguageDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left flex items-center gap-2 text-sm transition-all duration-200 font-medium ${
                          selectedLanguage === language.code
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                            : isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-blue-50'
                        }`}
                      >
                        <FlagDisplay code={language.code} fallbackText={language.code} />
                        <span className="truncate">{language.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
          </div>
          
          {/* Input Textarea + Suggestions */}
          <div className={`relative flex flex-col h-72 border-2 rounded-2xl overflow-hidden transition-all duration-300 shadow-inner ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600 focus-within:ring-4 focus-within:ring-blue-500/30 focus-within:border-blue-500'
              : 'bg-white border-gray-200 focus-within:ring-4 focus-within:ring-blue-500/20 focus-within:border-blue-500'
          } ${isLimitReached ? 'opacity-50' : ''}`}>
            <textarea
              value={inputText}
              onChange={(e) => { if (!isLimitReached && hasPaidPlan) setInputText(e.target.value); }}
              onKeyDown={handleSuggestionKeyDown}
              placeholder={isLimitReached ? `⚠️ ${t('daily_limit_reached')}` : t('type_or_paste')}
              disabled={isLimitReached || !hasPaidPlan}
              readOnly={!hasPaidPlan}
              className={`w-full ${showSuggestions && suggestions.length > 0 ? 'h-20' : 'flex-1'} p-4 md:p-5 resize-none focus:outline-none text-base md:text-lg bg-transparent ${
                isDarkMode
                  ? 'text-white placeholder-gray-400'
                  : 'placeholder-gray-400'
              } ${isLimitReached || !hasPaidPlan ? 'cursor-not-allowed' : ''}`}
              maxLength={appSettings?.maxTranslationChars || 500}
            />

            {/* Suggestions List — inside input box */}
            {showSuggestions && suggestions.length > 0 && (
              <div className={`flex-1 overflow-y-auto border-t ${
                isDarkMode ? 'border-gray-600' : 'border-gray-100'
              }`}>
                <div className={`px-4 py-1 text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>Suggestions</div>
                {suggestions.map((s, idx) => (
                  <button
                    key={s}
                    onMouseDown={() => handleSuggestionClick(s)}
                    className={`w-full px-4 py-2.5 text-left text-sm font-medium flex items-center gap-3 transition-all duration-150 ${
                      idx === selectedSuggestionIndex
                        ? (isDarkMode
                          ? 'bg-gradient-to-r from-blue-600/30 to-purple-600/20 text-blue-300'
                          : 'bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700')
                        : (isDarkMode
                          ? 'text-gray-300 hover:bg-gray-600/40'
                          : 'text-gray-600 hover:bg-gray-50')
                    }`}
                  >
                    <svg className={`w-4 h-4 flex-shrink-0 ${
                      idx === selectedSuggestionIndex
                        ? (isDarkMode ? 'text-blue-400' : 'text-blue-500')
                        : (isDarkMode ? 'text-gray-500' : 'text-gray-300')
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>{s}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Character Count */}
            <div className={`absolute bottom-2 right-2 text-xs font-medium px-2 py-1 rounded-lg ${
              isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
            }`}>
              {inputText.length}/{appSettings?.maxTranslationChars || 500}
            </div>
            
            {/* Microphone Button (Voice Input) - BOTTOM LEFT CORNER */}
            {/* Always renders the Pro-style button so free users see the same UI as Pro;
                glass paywall overlay above blocks interaction for free users. */}
            {appSettings.voiceInputEnabled && (
              <button
                onClick={hasPaidPlan ? toggleVoiceInput : undefined}
                className={`absolute bottom-3 left-3 p-3 rounded-xl transition-all shadow-lg transform hover:scale-105 ${
                  isListening
                    ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white animate-pulse scale-110'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-xl'
                }`}
                title={isListening ? "🎤 Stop listening" : "🎙️ Click to speak"}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>
            )}
            
            {inputText && !isLimitReached && (
              <button
                onClick={onClear}
                className={`absolute top-3 right-3 p-2 rounded-lg transition-all ${
                  isDarkMode
                    ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                    : 'bg-red-100 text-red-600 hover:bg-red-200'
                }`}
                title={t('clear_text')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Grammar correction note — stateless, reads directly from API response */}
          {translationResult?.correctedText && (
            <div
              className={`w-full mt-1.5 px-3 py-1.5 rounded-lg text-xs text-left ${
                isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-600'
              }`}
            >
              Searched: <span className="font-medium italic">"{translationResult.correctedText}"</span>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Target Language dropdown + Output textarea */}
        <div className="flex flex-col items-stretch">
          {/* Target Language Label (always Darija) */}
          <div className="mb-4 h-[52px] flex items-center">
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-semibold shadow-md ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-blue-200'
            }`}>
              <FlagDisplay code="ma" fallbackText="MA" />
              <span className="font-semibold">Darija</span>
            </div>
          </div>
          
          {/* Output Textarea */}
          <div className={`relative h-72 p-4 border-2 rounded-2xl transition-all duration-300 shadow-inner ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600'
              : 'bg-white border-gray-200'
          }`}>
            {/* Action buttons: Favorite, Like/Dislike, Learn - Top right */}
            {translationResult?.translatedText && (
              <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                {/* Favorite Heart Button */}
                {appSettings.favoritesEnabled && hasPaidPlan && (
                  <button
                    onClick={addToFavorites}
                    disabled={isFavorited}
                    className={`p-1.5 rounded-lg transition-all ${
                      isFavorited
                        ? 'text-red-500 scale-110'
                        : isDarkMode
                        ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600'
                        : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                    }`}
                    title={isFavorited ? pageLabels.added_to_favorites : pageLabels.add_to_favorites_tooltip}
                  >
                    <svg className="w-4 h-4" fill={isFavorited ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                )}
                
                {/* Like Button */}
                <button
                  onClick={() => submitFeedback('like')}
                  disabled={userFeedback !== null}
                  className={`p-1.5 rounded-lg transition-all ${
                    userFeedback === 'like'
                      ? 'text-green-500 bg-green-50 dark:bg-green-900/30'
                      : isDarkMode
                      ? 'text-gray-400 hover:text-green-400 hover:bg-gray-600'
                      : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
                  } ${userFeedback !== null && userFeedback !== 'like' ? 'opacity-50' : ''}`}
                  title={userFeedback === 'like' ? "You liked this" : "Good translation"}
                >
                  <svg className="w-4 h-4" fill={userFeedback === 'like' ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                  </svg>
                </button>

                {/* Dislike Button */}
                <button
                  onClick={() => submitFeedback('dislike')}
                  disabled={userFeedback !== null}
                  className={`p-1.5 rounded-lg transition-all ${
                    userFeedback === 'dislike'
                      ? 'text-red-500 bg-red-50 dark:bg-red-900/30'
                      : isDarkMode
                      ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600'
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  } ${userFeedback !== null && userFeedback !== 'dislike' ? 'opacity-50' : ''}`}
                  title={userFeedback === 'dislike' ? "You disliked this" : "Bad translation"}
                >
                  <svg className="w-4 h-4" fill={userFeedback === 'dislike' ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                  </svg>
                </button>
                
                {/* Save to Word Bank Button */}
                {hasPaidPlan && (
                  <button
                    onClick={markAsLearned}
                    disabled={isLearning}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                      isLearning
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                        : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
                    }`}
                    title={isLearning ? "Saved to Word Bank" : "Save to Word Bank"}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    {isLearning ? '✓ Saved' : 'Save'}
                  </button>
                )}
              </div>
            )}
            
            {/* Script Toggle: Latin / Arabic */}
            {translationResult?.translatedText && displayResult.latinScript && (
              <div className="absolute top-2 left-2 z-10 flex rounded-lg overflow-hidden border" style={{ borderColor: isDarkMode ? '#4b5563' : '#d1d5db' }}>
                {['latin','arabic'].map(mode => (
                  <button key={mode} onClick={() => setScriptView(mode)}
                    className={`px-2 py-0.5 text-xs font-medium transition-all ${
                      scriptView === mode
                        ? 'bg-blue-500 text-white'
                        : isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >{mode === 'latin' ? 'Aa' : 'عر'}</button>
                ))}
              </div>
            )}

            {/* Translation Content */}
            <div className="pt-10 h-full overflow-auto">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="relative w-10 h-10 mb-2">
                    <div className="absolute inset-0 rounded-full border-4 border-blue-200 dark:border-blue-900"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                  </div>
                  <p className={`text-sm font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-pulse`}>
                    {t('translating')}
                  </p>
                </div>
              ) : (translationResult?.translatedText || disambiguationSelected) ? (
                <div className="w-full">
                  {/* Clean Translation Display - Latin | Arabic on same line */}
                  <div className="text-center mb-2">
                    <p className={`text-base font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {['party','confetti','pulse','voltage'].includes(displayResult.specialStyle?.style) ? (
                        <span className={displayResult.specialStyle.style === 'party' ? 'party-wrap' : displayResult.specialStyle.style === 'confetti' ? 'confetti-wrap' : displayResult.specialStyle.style === 'pulse' ? 'pulse-wrap' : 'voltage-wrap'}>
                          {scriptView === 'latin' && displayResult.latinScript && (
                            <span className={`relative z-10 special-size-${displayResult.specialStyle.size} ${isDarkMode ? 'text-green-400' : 'text-green-700'}`} style={{fontWeight: 700}}>
                              {displayResult.latinScript}
                            </span>
                          )}
                          {scriptView === 'arabic' && displayResult.translatedText && (
                            <span dir="rtl" className={`relative z-10 special-size-${displayResult.specialStyle.size} ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`} style={{fontWeight: 700}}>
                              {displayResult.translatedText}
                            </span>
                          )}
                          {displayResult.specialStyle.style === 'party' && partyDots.map((dot, i) => (
                            <span key={i} className="party-dot" style={dot.style} />
                          ))}
                          {displayResult.specialStyle.style === 'confetti' && confettiPieces.map((piece, i) => (
                            <span key={i} className="confetti-piece" style={piece.style} />
                          ))}
                          {displayResult.specialStyle.style === 'voltage' && voltageDots.map((dot, i) => (
                            <span key={i} className="voltage-dot" style={dot.style} />
                          ))}
                        </span>
                      ) : (
                        <>
                          {scriptView === 'latin' && displayResult.latinScript && (
                            <span className={
                              displayResult.specialStyle
                                ? `special-text-${displayResult.specialStyle.style} special-size-${displayResult.specialStyle.size}`
                                : (isDarkMode ? 'text-green-400' : 'text-green-700')
                            }>
                              {displayResult.latinScript}
                            </span>
                          )}
                          {scriptView === 'arabic' && (
                            <span dir="rtl" className={
                              displayResult.specialStyle
                                ? `special-text-${displayResult.specialStyle.style} special-size-${displayResult.specialStyle.size}`
                                : (isDarkMode ? 'text-blue-400' : 'text-blue-700')
                            }>
                              {displayResult.translatedText}
                            </span>
                          )}
                        </>
                      )}
                    </p>
                  </div>

                  {/* Gender & Number Icons - Compact row */}
                  {(appSettings.genderIconsEnabled && displayResult.gender) || (appSettings.numberIconsEnabled && displayResult.number) ? (
                    <div className="flex justify-center gap-2 mb-2">
                      {appSettings.genderIconsEnabled && displayResult.gender && appSettings.genderIconConfig && (
                        <span
                          className="inline-block px-2 py-0.5 rounded text-xs font-bold"
                          style={{
                            backgroundColor: displayResult.gender === 'male'
                              ? (appSettings.genderIconConfig.male?.bgColor || '#3b82f6')
                              : displayResult.gender === 'female'
                              ? (appSettings.genderIconConfig.female?.bgColor || '#ec4899')
                              : (appSettings.genderIconConfig.both?.bgColor || '#a855f7'),
                            color: displayResult.gender === 'male'
                              ? (appSettings.genderIconConfig.male?.textColor || '#1e40af')
                              : displayResult.gender === 'female'
                              ? (appSettings.genderIconConfig.female?.textColor || '#9f1239')
                              : (appSettings.genderIconConfig.both?.textColor || '#6b21a8')
                          }}
                        >
                          {displayResult.gender === 'male'
                            ? (appSettings.genderIconConfig.male?.icon || '♂ M')
                            : displayResult.gender === 'female'
                            ? (appSettings.genderIconConfig.female?.icon || '♀ F')
                            : (appSettings.genderIconConfig.both?.icon || '👥 Both')}
                        </span>
                      )}
                      {appSettings.numberIconsEnabled && displayResult.number && appSettings.numberIconConfig && (
                        <span
                          className="inline-block px-2 py-0.5 rounded text-xs font-bold"
                          style={{
                            backgroundColor: displayResult.number === 'singular'
                              ? (appSettings.numberIconConfig.singular?.bgColor || '#10b981')
                              : (appSettings.numberIconConfig.plural?.bgColor || '#f97316'),
                            color: displayResult.number === 'singular'
                              ? (appSettings.numberIconConfig.singular?.textColor || '#065f46')
                              : (appSettings.numberIconConfig.plural?.textColor || '#9a3412')
                          }}
                        >
                          {displayResult.number === 'singular'
                            ? (appSettings.numberIconConfig.singular?.icon || 'S')
                            : (appSettings.numberIconConfig.plural?.icon || 'P')}
                        </span>
                      )}
                    </div>
                  ) : null}

                  {/* Variants Section - Clean single line format */}
                  {displayResult.variants && displayResult.variants.length > 0 && (
                    <div className={`border-t pt-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('other_meanings')}
                      </p>
                      <div className="space-y-1">
                        {[...displayResult.variants].sort((a, b) => {
                          const aPlural = a.number === 'plural' ? 1 : 0;
                          const bPlural = b.number === 'plural' ? 1 : 0;
                          if (aPlural !== bPlural) return aPlural - bPlural;
                          const aFemale = a.gender === 'female' ? 0 : 1;
                          const bFemale = b.gender === 'female' ? 0 : 1;
                          return aFemale - bFemale;
                        }).slice(0, showAllVariants ? 100 : 3).map((v, i) => {
                          const isGenderMatch = genderFilter && v.gender === genderFilter;
                          return (
                          <p key={i} className={`text-sm px-2 py-1 rounded ${isGenderMatch ? (isDarkMode ? 'bg-green-900/40 border border-green-500/50' : 'bg-green-50 border border-green-300') : ''} ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {isGenderMatch && <span className="mr-1 text-green-500">★</span>}
                            {scriptView === 'latin' && <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>{v.latinScript}</span>}
                            {scriptView === 'arabic' && <span dir="rtl" className={isDarkMode ? 'text-blue-400' : 'text-blue-600'}>{v.translatedText}</span>}
                            {appSettings.genderIconsEnabled && v.gender && appSettings.genderIconConfig && (
                              <span
                                className="inline-block ml-2 px-1.5 py-0.5 rounded text-xs font-bold"
                                style={{
                                  backgroundColor: v.gender === 'male'
                                    ? (appSettings.genderIconConfig.male?.bgColor || '#3b82f6')
                                    : v.gender === 'female'
                                    ? (appSettings.genderIconConfig.female?.bgColor || '#ec4899')
                                    : (appSettings.genderIconConfig.both?.bgColor || '#a855f7'),
                                  color: v.gender === 'male'
                                    ? (appSettings.genderIconConfig.male?.textColor || '#1e40af')
                                    : v.gender === 'female'
                                    ? (appSettings.genderIconConfig.female?.textColor || '#9f1239')
                                    : (appSettings.genderIconConfig.both?.textColor || '#6b21a8')
                                }}
                              >
                                {v.gender === 'male'
                                  ? (appSettings.genderIconConfig.male?.icon || '♂ M')
                                  : v.gender === 'female'
                                  ? (appSettings.genderIconConfig.female?.icon || '♀ F')
                                  : (appSettings.genderIconConfig.both?.icon || '👥 Both')}
                              </span>
                            )}
                            {appSettings.numberIconsEnabled && v.number && appSettings.numberIconConfig && (
                              <span
                                className="inline-block ml-1 px-1.5 py-0.5 rounded text-xs font-bold"
                                style={{
                                  backgroundColor: v.number === 'singular'
                                    ? (appSettings.numberIconConfig.singular?.bgColor || '#10b981')
                                    : (appSettings.numberIconConfig.plural?.bgColor || '#f97316'),
                                  color: v.number === 'singular'
                                    ? (appSettings.numberIconConfig.singular?.textColor || '#065f46')
                                    : (appSettings.numberIconConfig.plural?.textColor || '#9a3412')
                                }}
                              >
                                {v.number === 'singular'
                                  ? (appSettings.numberIconConfig.singular?.icon || 'S')
                                  : (appSettings.numberIconConfig.plural?.icon || 'P')}
                              </span>
                            )}
                          </p>
                          );
                        })}
                      </div>
                      {displayResult.variants.length > 3 && !showAllVariants && (
                        <button
                          onClick={() => setShowAllVariants(true)}
                          className={`text-xs mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline`}
                        >
                          {t('show_more').replace('{count}', displayResult.variants.length - 3)}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {t('start_translating')}
                  </h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('type_in_box')}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Action buttons for desktop - Copy & Play */}
          {translationResult?.translatedText && (
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => {
                  const latin = displayResult.latinScript || '';
                  const arabic = displayResult.translatedText || '';
                  const text = scriptView === 'latin' ? latin : arabic;
                  copyToClipboard(text);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all font-semibold text-white shadow-lg hover:shadow-xl bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('copy')}
              </button>

              {appSettings.audioGloballyEnabled && hasPaidPlan && translationResult.audioUrl && translationResult.audioUrl.trim() !== '' && (
                <button
                  onClick={() => playAudio(translationResult.translatedText, translationResult.audioUrl)}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-colors ${
                    isDarkMode
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                  {t('audio')}
                </button>
              )}
            </div>
          )}
        </div>
        </div>
        {/* Pro paywall overlay — inside the grid (which is position:relative)
            so the absolute overlay anchors to the grid's bounding box. The button
            at top:50%/left:50% lands exactly in the gap BETWEEN input and output. */}
        {!hasPaidPlan && (
          <div className="hidden lg:block absolute inset-0 z-10 rounded-3xl cursor-not-allowed"
               style={{ backdropFilter: 'blur(0.6px)', WebkitBackdropFilter: 'blur(0.6px)', backgroundColor: isDarkMode ? 'rgba(17,24,39,0.08)' : 'rgba(255,255,255,0.06)' }}>
            <button
              onClick={() => { setInitialProfileTab('membership'); setCurrentView('profile'); }}
              style={{ top: '30%', left: '58%', transform: 'translate(-50%, -50%)' }}
              className="absolute px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold shadow-2xl ring-4 ring-white/30 hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <span className="text-xl">🔓</span>
              <span>Upgrade to Pro</span>
            </button>
          </div>
        )}
      </div>

      {/* Desktop Quick Actions Bar - Icons below translation */}
      <div className={`hidden lg:flex justify-center gap-4 mt-6 p-4 rounded-2xl ${
        isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'
      }`}>
        {/* Explore */}
        <button
          onClick={() => {
            // Free users / anonymous: icons are visual-only (no-op).
            // The page-wide Pro paywall overlay handles the upgrade prompt.
            if (hasPaidPlan) setCurrentView('daily-words');
          }}
          className={`relative flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all hover:scale-105 ${
            isDarkMode
              ? 'hover:bg-gray-700 text-gray-300 hover:text-white'
              : 'hover:bg-white hover:shadow-md text-gray-600 hover:text-gray-900'
          }`}
        >
          <span style={{display:'inline-flex',width:32,height:32,alignItems:'center',justifyContent:'center',flexShrink:0,backgroundColor:'#f3f4f6',borderRadius:8}}>
            {appSettings.explore_icon_url && <img src={`${API.replace('/api', '')}${appSettings.explore_icon_url}`} alt="" width="32" height="32" loading="eager" decoding="async" style={{width:32,height:32,objectFit:'contain'}} />}
          </span>
          <span className="text-xs font-medium">{t('explore') || 'Explore'}</span>
        </button>

        {/* Favorites */}
        <button
          onClick={() => {
            if (hasPaidPlan) setCurrentView('favorites');
          }}
          className={`relative flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all hover:scale-105 ${
            isDarkMode
              ? 'hover:bg-gray-700 text-gray-300 hover:text-white'
              : 'hover:bg-white hover:shadow-md text-gray-600 hover:text-gray-900'
          }`}
        >
          <span style={{display:'inline-flex',width:32,height:32,alignItems:'center',justifyContent:'center',flexShrink:0,backgroundColor:'#f3f4f6',borderRadius:8}}>
            {appSettings.favorites_icon_url && <img src={`${API.replace('/api', '')}${appSettings.favorites_icon_url}`} alt="" width="32" height="32" loading="eager" decoding="async" style={{width:32,height:32,objectFit:'contain'}} />}
          </span>
          <span className="text-xs font-medium">{t('favorites')}</span>
          {/* Lock icon for Free users */}
        </button>

        {/* Request Word */}
        <button
          onClick={() => {
            if (hasPaidPlan) setCurrentView('request');
          }}
          className={`relative flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all hover:scale-105 ${
            isDarkMode
              ? 'hover:bg-gray-700 text-gray-300 hover:text-white'
              : 'hover:bg-white hover:shadow-md text-gray-600 hover:text-gray-900'
          }`}
        >
          <span style={{display:'inline-flex',width:32,height:32,alignItems:'center',justifyContent:'center',flexShrink:0,backgroundColor:'#f3f4f6',borderRadius:8}}>
            {appSettings.request_word_icon_url && <img src={`${API.replace('/api', '')}${appSettings.request_word_icon_url}`} alt="" width="32" height="32" loading="eager" decoding="async" style={{width:32,height:32,objectFit:'contain'}} />}
          </span>
          <span className="text-xs font-medium">{t('request_word')}</span>
          {/* Lock icon for Free users */}
        </button>

        {/* Word Bank */}
        <button
          onClick={() => {
            if (hasPaidPlan) setCurrentView('progress');
          }}
          className={`relative flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all hover:scale-105 ${
            isDarkMode
              ? 'hover:bg-gray-700 text-gray-300 hover:text-white'
              : 'hover:bg-white hover:shadow-md text-gray-600 hover:text-gray-900'
          }`}
        >
          <span style={{display:'inline-flex',width:32,height:32,alignItems:'center',justifyContent:'center',flexShrink:0,backgroundColor:'#f3f4f6',borderRadius:8}}>
            {appSettings.word_bank_icon_url && <img src={`${API.replace('/api', '')}${appSettings.word_bank_icon_url}`} alt="" width="32" height="32" loading="eager" decoding="async" style={{width:32,height:32,objectFit:'contain'}} />}
          </span>
          <span className="text-xs font-medium">{t('word_bank') || 'Word Bank'}</span>
          {/* Lock icon for Free users */}
        </button>
      </div>


      {/* Desktop: Alternative translations (disambiguation) */}
      {translationResult?.disambiguation && translationResult?.disambiguationOptions?.length > 0 && (
        <div className={`hidden lg:block mt-3 p-3 rounded-xl border ${
          isDarkMode ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-200'
        }`}>
          <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Also means:
          </p>
          <div className="space-y-1.5">
            {translationResult.disambiguationOptions
              .filter(opt => {
                if (disambiguationSelected) return opt.id !== disambiguationSelected.id;
                return opt.translatedText !== displayResult.translatedText || opt.note !== (translationResult.note || '');
              })
              .map((option, i) => (
                <button
                  key={option.id || i}
                  onClick={() => handleDisambiguationSelect(option)}
                  disabled={disambiguationLoading}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-all text-sm ${
                    isDarkMode
                      ? 'hover:bg-gray-700 text-gray-300 border border-gray-600 hover:border-blue-500'
                      : 'hover:bg-blue-50 text-gray-700 border border-gray-200 hover:border-blue-400'
                  } ${disambiguationLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className="font-medium">{inputText}</span>
                  <span className={`ml-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>({option.note})</span>
                  <span className={`ml-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>→</span>
                  <span className={`ml-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    {option.latinScript || option.translatedText}
                  </span>
                </button>
              ))
            }
          </div>
        </div>
      )}

      {/* Desktop: Note card — explains the current word/meaning (HTML from admin) */}
      {displayResult?.note && !translationResult?.disambiguation && (
        <div className={`hidden lg:block mt-2 px-4 py-3 rounded-xl border text-sm ${
          isDarkMode ? 'bg-gray-800/40 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'
        }`}>
          <div className="flex flex-col items-center text-center gap-1">
            <span>ℹ️</span>
            <div className="note-content" style={{ lineHeight: '1.8', wordSpacing: '0.05em' }} dangerouslySetInnerHTML={{ __html: displayResult.note }} />
          </div>
        </div>
      )}

      {/* Mobile layout - single column */}
      {/* `relative` makes the grid the positioning context for the Pro paywall overlay below. */}
      <div className="lg:hidden grid grid-cols-1 gap-3 relative">
        {/* SOURCE LANGUAGE INPUT */}
        <div className="space-y-2">
          {/* Language Selector - Mobile only (desktop version is in header above) */}
          <div className="lg:hidden flex items-center justify-between mb-2">
            <div className="relative">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all duration-300 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                    : 'bg-white border-blue-200 hover:bg-blue-50 hover:border-blue-300'
                }`}
              >
                <FlagDisplay code={selectedLang?.code} fallbackText={selectedLang?.code} />
                <span className="font-semibold hidden sm:inline truncate">
                  {selectedLang?.name || "Auto-detect"}
                </span>
                <svg className={`w-4 h-4 transition-transform duration-300 ${isLanguageDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isLanguageDropdownOpen && (
                <div className={`absolute top-full left-0 mt-2 w-56 rounded-2xl shadow-2xl max-h-64 overflow-y-auto z-50 border-2 ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-blue-200'
                }`}>
                  <div className="py-2">
                    {languages.map((language) => (
                      <button
                        key={language.id}
                        onClick={() => {
                          setSelectedLanguage(language.code);
                          setIsLanguageDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left flex items-center gap-2 text-sm transition-all duration-200 font-medium ${
                          selectedLanguage === language.code
                            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                            : isDarkMode
                            ? 'text-gray-200 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-blue-50'
                        }`}
                      >
                        <FlagDisplay code={language.code} fallbackText={language.code} />
                        <span className="truncate">{language.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <span className={`text-sm font-semibold ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {selectedLanguage === 'ma' ? 'AR' : 'EN'} Source Text
            </span>
          </div>

          
          <div className={`relative flex flex-col h-72 border-2 rounded-xl overflow-hidden transition-all duration-300 shadow-inner ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600 focus-within:ring-4 focus-within:ring-blue-500/30 focus-within:border-blue-500'
              : 'bg-white border-gray-200 focus-within:ring-4 focus-within:ring-blue-500/20 focus-within:border-blue-500'
          } ${isLimitReached ? 'opacity-50' : ''}`}>
            <textarea
              value={inputText}
              onChange={(e) => { if (!isLimitReached && hasPaidPlan) setInputText(e.target.value); }}
              onKeyDown={handleSuggestionKeyDown}
              placeholder={isLimitReached ? `⚠️ ${t('daily_limit_reached')}` : t('type_or_paste')}
              disabled={isLimitReached || !hasPaidPlan}
              readOnly={!hasPaidPlan}
              className={`w-full ${showSuggestions && suggestions.length > 0 ? 'h-16' : 'flex-1'} p-3 md:p-5 resize-none focus:outline-none text-sm md:text-lg bg-transparent ${
                isDarkMode
                  ? 'text-white placeholder-gray-400'
                  : 'placeholder-gray-400'
              } ${isLimitReached || !hasPaidPlan ? 'cursor-not-allowed' : ''}`}
              maxLength={appSettings?.maxTranslationChars || 500}
            />

            {/* Suggestions List — inside input box, mobile */}
            {showSuggestions && suggestions.length > 0 && (
              <div className={`flex-1 overflow-y-auto border-t ${
                isDarkMode ? 'border-gray-600' : 'border-gray-100'
              }`}>
                <div className={`px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`}>Suggestions</div>
                {suggestions.map((s, idx) => (
                  <button
                    key={s}
                    onMouseDown={() => handleSuggestionClick(s)}
                    className={`w-full px-3 py-2 text-left text-xs font-medium flex items-center gap-2 transition-all duration-150 ${
                      idx === selectedSuggestionIndex
                        ? (isDarkMode
                          ? 'bg-gradient-to-r from-blue-600/30 to-purple-600/20 text-blue-300'
                          : 'bg-gradient-to-r from-blue-50 to-purple-50 text-blue-700')
                        : (isDarkMode
                          ? 'text-gray-300 hover:bg-gray-600/40'
                          : 'text-gray-600 hover:bg-gray-50')
                    }`}
                  >
                    <svg className={`w-3.5 h-3.5 flex-shrink-0 ${
                      idx === selectedSuggestionIndex
                        ? (isDarkMode ? 'text-blue-400' : 'text-blue-500')
                        : (isDarkMode ? 'text-gray-500' : 'text-gray-300')
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span className="truncate">{s}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Character Count */}
            <div className={`absolute bottom-2 right-2 text-xs font-medium px-2 py-1 rounded-lg ${
              isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
            }`}>
              {inputText.length}/{appSettings?.maxTranslationChars || 500}
            </div>

            {/* Microphone Button (Voice Input) - BOTTOM LEFT CORNER */}
            {appSettings.voiceInputEnabled && (
              <>
                {hasPaidPlan ? (
                  <button
                    onClick={toggleVoiceInput}
                    className={`absolute bottom-5 left-5 p-3 rounded-xl transition-all shadow-lg transform hover:scale-105 ${
                      isListening
                        ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white animate-pulse scale-110'
                        : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:shadow-xl'
                    }`}
                    title={isListening ? "🎤 Stop listening" : "🎙️ Click to speak"}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      if (!userToken) {
                        showNotification('Please login to use voice input', 'info');
                      } else {
                        setInitialProfileTab('membership');
                        setCurrentView('profile');
                        showNotification(pageLabels.voice_locked_notification, 'info');
                      }
                    }}
                    className={`absolute bottom-5 left-5 p-3 rounded-xl opacity-70 cursor-not-allowed relative shadow-lg ${
                      isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                    }`}
                    title={!userToken ? pageLabels.voice_locked_guest : pageLabels.voice_locked_user}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    {/* Orange/Yellow Lock Badge - Positioned to stay inside button area */}
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-orange-400 to-yellow-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-lg">
                      <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </button>
                )}
              </>
            )}
            
            {inputText && !isLimitReached && (
              <button
                onClick={onClear}
                className={`absolute bottom-14 right-4 p-3 rounded-xl transition-all shadow-lg ${
                  isDarkMode
                    ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                    : 'bg-red-100 text-red-600 hover:bg-red-200'
                }`}
                title={t('clear_text')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Grammar correction note — stateless, reads directly from API response (Mobile) */}
          {translationResult?.correctedText && (
            <div
              className={`w-full mt-1.5 px-3 py-1.5 rounded-lg text-xs text-left ${
                isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-600'
              }`}
            >
              Searched: <span className="font-medium italic">"{translationResult.correctedText}"</span>
            </div>
          )}
        </div>


        {/* DARIJA/ENGLISH TRANSLATION OUTPUT */}
        <div className="space-y-2">
          <div className="relative">
            <div className={`relative h-72 p-3 md:p-5 border-2 rounded-xl transition-all duration-300 shadow-inner overflow-y-auto ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600'
                : 'bg-white border-gray-200'
            }`}>
              {/* Script Toggle: Latin / Arabic - Mobile */}
              {translationResult?.translatedText && displayResult.latinScript && (
                <div className="absolute top-2 left-2 z-10 flex rounded-lg overflow-hidden border" style={{ borderColor: isDarkMode ? '#4b5563' : '#d1d5db' }}>
                  {['latin','arabic'].map(mode => (
                    <button key={mode} onClick={() => setScriptView(mode)}
                      className={`px-2 py-0.5 text-xs font-medium transition-all ${
                        scriptView === mode
                          ? 'bg-blue-500 text-white'
                          : isDarkMode ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >{mode === 'latin' ? 'Aa' : '\u0639\u0631'}</button>
                  ))}
                </div>
              )}

              {isLoading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="relative w-16 h-16 mb-4">
                    <div className="absolute inset-0 rounded-full border-4 border-blue-200 dark:border-blue-900"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                  </div>
                  <p className={`text-lg font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent animate-pulse`}>
                    {t('translating')}
                  </p>
                </div>
              ) : translationResult?.notFound ? (
                <div className="w-full text-center py-6">
                  {/* Not Found - Show simple message */}
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-100'
                  }`}>
                    <svg className={`w-8 h-8 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  
                  <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    Translation Not Found
                  </h3>
                  
                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    We don't have a translation for "<strong>{translationResult.searchedText}</strong>" yet.
                  </p>
                  
                  {appSettings.wordSuggestionsEnabled && (
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Click on <strong>"📝 Request a New Word"</strong> button to send it to admin.
                    </p>
                  )}
                </div>
              ) : translationResult?.limitExceeded ? (
                <div className="w-full text-center py-8">
                  {/* Usage Limit Exceeded - Simple message, modal handles details */}
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDarkMode ? 'bg-orange-900/30' : 'bg-orange-100'
                  }`}>
                    <svg className={`w-8 h-8 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  
                  <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    Daily Translation Limit Reached
                  </h3>
                  
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    You've used all {translationResult.limit} free translations for today
                  </p>
                  
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {!userToken ? pageLabels.guest_empty_state_hint : pageLabels.upgrade_cta_pro_text}
                  </p>
                </div>
              ) : (translationResult?.translatedText || disambiguationSelected) ? (
                <div className="w-full">
                  {/* Clean Translation Display - Respects scriptView toggle */}
                  <div className={`text-center mb-3 ${translationResult?.translatedText && displayResult.latinScript ? 'pt-8' : ''}`}>
                    <p className={`text-lg md:text-xl font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {scriptView === 'latin' && displayResult.latinScript && (
                        <span className={isDarkMode ? 'text-green-400' : 'text-green-700'}>
                          {displayResult.latinScript}
                        </span>
                      )}
                      {scriptView === 'arabic' && (
                        <span dir="rtl" className={isDarkMode ? 'text-blue-400' : 'text-blue-700'}>
                          {displayResult.translatedText}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Gender & Number Icons - Compact row */}
                  {(appSettings.genderIconsEnabled && displayResult.gender) || (appSettings.numberIconsEnabled && displayResult.number) ? (
                    <div className="flex justify-center gap-2 mb-3">
                      {appSettings.genderIconsEnabled && displayResult.gender && appSettings.genderIconConfig && (
                        <span
                          className="inline-block px-2 py-0.5 rounded text-xs font-bold"
                          style={{
                            backgroundColor: displayResult.gender === 'male'
                              ? (appSettings.genderIconConfig.male?.bgColor || '#3b82f6')
                              : displayResult.gender === 'female'
                              ? (appSettings.genderIconConfig.female?.bgColor || '#ec4899')
                              : (appSettings.genderIconConfig.both?.bgColor || '#a855f7'),
                            color: displayResult.gender === 'male'
                              ? (appSettings.genderIconConfig.male?.textColor || '#1e40af')
                              : displayResult.gender === 'female'
                              ? (appSettings.genderIconConfig.female?.textColor || '#9f1239')
                              : (appSettings.genderIconConfig.both?.textColor || '#6b21a8')
                          }}
                        >
                          {displayResult.gender === 'male'
                            ? (appSettings.genderIconConfig.male?.icon || '♂ M')
                            : displayResult.gender === 'female'
                            ? (appSettings.genderIconConfig.female?.icon || '♀ F')
                            : (appSettings.genderIconConfig.both?.icon || '👥 Both')}
                        </span>
                      )}
                      {appSettings.numberIconsEnabled && displayResult.number && appSettings.numberIconConfig && (
                        <span
                          className="inline-block px-2 py-0.5 rounded text-xs font-bold"
                          style={{
                            backgroundColor: displayResult.number === 'singular'
                              ? (appSettings.numberIconConfig.singular?.bgColor || '#10b981')
                              : (appSettings.numberIconConfig.plural?.bgColor || '#f97316'),
                            color: displayResult.number === 'singular'
                              ? (appSettings.numberIconConfig.singular?.textColor || '#065f46')
                              : (appSettings.numberIconConfig.plural?.textColor || '#9a3412')
                          }}
                        >
                          {displayResult.number === 'singular'
                            ? (appSettings.numberIconConfig.singular?.icon || 'S')
                            : (appSettings.numberIconConfig.plural?.icon || 'P')}
                        </span>
                      )}
                    </div>
                  ) : null}

                  {/* Variants Section - Clean single line format */}
                  {displayResult.variants && displayResult.variants.length > 0 && (
                    <div className={`border-t pt-3 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('other_meanings')}
                      </p>
                      <div className="space-y-1">
                        {[...displayResult.variants].sort((a, b) => {
                          const aPlural = a.number === 'plural' ? 1 : 0;
                          const bPlural = b.number === 'plural' ? 1 : 0;
                          if (aPlural !== bPlural) return aPlural - bPlural;
                          const aFemale = a.gender === 'female' ? 0 : 1;
                          const bFemale = b.gender === 'female' ? 0 : 1;
                          return aFemale - bFemale;
                        }).slice(0, showAllVariants ? 100 : 3).map((v, i) => {
                          const isGenderMatch = genderFilter && v.gender === genderFilter;
                          return (
                          <p key={i} className={`text-sm px-2 py-1 rounded ${isGenderMatch ? (isDarkMode ? 'bg-green-900/40 border border-green-500/50' : 'bg-green-50 border border-green-300') : ''} ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {isGenderMatch && <span className="mr-1 text-green-500">★</span>}
                            {scriptView === 'latin' && <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>{v.latinScript}</span>}
                            {scriptView === 'arabic' && <span dir="rtl" className={isDarkMode ? 'text-blue-400' : 'text-blue-600'}>{v.translatedText}</span>}
                            {appSettings.genderIconsEnabled && v.gender && appSettings.genderIconConfig && (
                              <span
                                className="inline-block ml-2 px-1.5 py-0.5 rounded text-xs font-bold"
                                style={{
                                  backgroundColor: v.gender === 'male'
                                    ? (appSettings.genderIconConfig.male?.bgColor || '#3b82f6')
                                    : v.gender === 'female'
                                    ? (appSettings.genderIconConfig.female?.bgColor || '#ec4899')
                                    : (appSettings.genderIconConfig.both?.bgColor || '#a855f7'),
                                  color: v.gender === 'male'
                                    ? (appSettings.genderIconConfig.male?.textColor || '#1e40af')
                                    : v.gender === 'female'
                                    ? (appSettings.genderIconConfig.female?.textColor || '#9f1239')
                                    : (appSettings.genderIconConfig.both?.textColor || '#6b21a8')
                                }}
                              >
                                {v.gender === 'male'
                                  ? (appSettings.genderIconConfig.male?.icon || '♂ M')
                                  : v.gender === 'female'
                                  ? (appSettings.genderIconConfig.female?.icon || '♀ F')
                                  : (appSettings.genderIconConfig.both?.icon || '👥 Both')}
                              </span>
                            )}
                            {appSettings.numberIconsEnabled && v.number && appSettings.numberIconConfig && (
                              <span
                                className="inline-block ml-1 px-1.5 py-0.5 rounded text-xs font-bold"
                                style={{
                                  backgroundColor: v.number === 'singular'
                                    ? (appSettings.numberIconConfig.singular?.bgColor || '#10b981')
                                    : (appSettings.numberIconConfig.plural?.bgColor || '#f97316'),
                                  color: v.number === 'singular'
                                    ? (appSettings.numberIconConfig.singular?.textColor || '#065f46')
                                    : (appSettings.numberIconConfig.plural?.textColor || '#9a3412')
                                }}
                              >
                                {v.number === 'singular'
                                  ? (appSettings.numberIconConfig.singular?.icon || 'S')
                                  : (appSettings.numberIconConfig.plural?.icon || 'P')}
                              </span>
                            )}
                          </p>
                          );
                        })}
                      </div>
                      {displayResult.variants.length > 3 && !showAllVariants && (
                        <button
                          onClick={() => setShowAllVariants(true)}
                          className={`text-xs mt-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline`}
                        >
                          {t('show_more').replace('{count}', displayResult.variants.length - 3)}
                        </button>
                      )}
                    </div>
                  )}
                  
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
                  <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {t('start_translating')}
                  </h3>
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('type_in_box')}
                  </p>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    <p className="font-semibold mb-1">Try these examples:</p>
                    <p>"hello" • "thank you" • "how are you"</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Small copy button inside output - Desktop only */}
            {translationResult?.translatedText && (
              <button
                onClick={() => {
                  const latin = displayResult.latinScript || '';
                  const arabic = displayResult.translatedText || '';
                  const text = scriptView === 'latin' ? latin : arabic;
                  copyToClipboard(text);
                }}
                className={`hidden md:block absolute bottom-3 md:bottom-4 right-3 md:right-4 p-3 rounded-xl transition-all shadow-lg transform hover:scale-110 ${
                  isDarkMode
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                }`}
                title="Copy translation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Action Buttons — between translation box and Copy */}
          {translationResult?.translatedText && (
            <div className="flex items-center justify-center gap-1.5 py-1">
                    {/* Favorite Heart Button */}
                    {appSettings.favoritesEnabled && (
                      <>
                        {hasPaidPlan ? (
                            <button
                              onClick={addToFavorites}
                              disabled={isFavorited}
                              className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                                isFavorited
                                  ? 'text-red-500 scale-110'
                                  : isDarkMode
                                  ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600'
                                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                              }`}
                              title={isFavorited ? pageLabels.added_to_favorites : pageLabels.add_to_favorites_tooltip}
                            >
                              <svg className="w-5 h-5" fill={isFavorited ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                              </svg>
                            </button>
                          ) : (
                            <div className="relative">
                              <button
                                onClick={() => {
                                  if (!userToken) {
                                    showNotification('Please login to save favorites', 'info');
                                  } else {
                                    setInitialProfileTab('membership');
                                    setCurrentView('profile');
                                    showNotification(pageLabels.favorites_locked_notification, 'info');
                                  }
                                }}
                                className={`flex-shrink-0 p-2 rounded-lg opacity-40 cursor-not-allowed ${
                                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                }`}
                                title={!userToken ? "🔒 Login to use favorites" : "🔒 Upgrade to Pro for favorites"}
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                              </button>
                              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                🔒
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      {/* Audio Button */}
                      {appSettings.audioGloballyEnabled && translationResult.audioUrl && translationResult.audioUrl.trim() !== '' && (
                        <>
                          {hasPaidPlan ? (
                            <button
                              onClick={() => playAudio(translationResult.translatedText, translationResult.audioUrl)}
                              className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                                isDarkMode
                                ? 'text-blue-400 hover:text-blue-300 hover:bg-gray-600'
                                : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                              }`}
                              title="Play audio"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M9 12h6m-3 3v6m0-12V3" />
                              </svg>
                            </button>
                          ) : (
                            <div className="relative">
                              <button
                                onClick={() => {
                                  if (!userToken) {
                                    showNotification('Please login to use audio', 'info');
                                  } else {
                                    setInitialProfileTab('membership');
                                    setCurrentView('profile');
                                    showNotification(pageLabels.audio_locked_notification, 'info');
                                  }
                                }}
                                className={`flex-shrink-0 p-2 rounded-lg opacity-40 cursor-not-allowed ${
                                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                                }`}
                                title={!userToken ? pageLabels.audio_locked_guest : pageLabels.audio_locked_user}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M9 12h6m-3 3v6m0-12V3" />
                                </svg>
                              </button>
                              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                🔒
                              </span>
                            </div>
                          )}
                        </>
                      )}

                      {/* Like/Dislike Buttons */}
                      {appSettings.feedbackEnabled && (
                        <>
                          <button
                            onClick={() => submitFeedback('like')}
                            disabled={userFeedback !== null}
                            className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                              userFeedback === 'like'
                                ? 'text-green-500 scale-110'
                                : isDarkMode
                                ? 'text-gray-400 hover:text-green-400 hover:bg-gray-600'
                                : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
                            } ${userFeedback !== null && userFeedback !== 'like' ? 'opacity-50' : ''}`}
                            title={userFeedback === 'like' ? "You liked this" : "Like this translation"}
                          >
                            <svg className="w-5 h-5" fill={userFeedback === 'like' ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                            </svg>
                          </button>
                          <button
                            onClick={() => submitFeedback('dislike')}
                            disabled={userFeedback !== null}
                            className={`flex-shrink-0 p-2 rounded-lg transition-all ${
                              userFeedback === 'dislike'
                                ? 'text-red-500 scale-110'
                                : isDarkMode
                                ? 'text-gray-400 hover:text-red-400 hover:bg-gray-600'
                                : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                            } ${userFeedback !== null && userFeedback !== 'dislike' ? 'opacity-50' : ''}`}
                            title={userFeedback === 'dislike' ? "You disliked this" : "Dislike this translation"}
                          >
                            <svg className="w-5 h-5" fill={userFeedback === 'dislike' ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                            </svg>
                          </button>
                        </>
                      )}

                      {/* Save to Word Bank */}
                      {userToken && hasPaidPlan && !translationWordsToday.limit_reached ? (
                        <button
                          onClick={markAsLearned}
                          disabled={isLearning}
                          className={`flex-shrink-0 px-2 py-1 rounded-lg transition-all text-xs font-medium ${
                            isLearning
                              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                              : isDarkMode
                              ? 'bg-purple-600 text-white hover:bg-purple-700'
                              : 'bg-purple-500 text-white hover:bg-purple-600'
                          }`}
                          title={isLearning ? "Saved to Word Bank" : `Save to Word Bank (${translationWordsToday.count || 0}/5 today)`}
                        >
                          {isLearning ? '✓' : '📚'}
                        </button>
                      ) : translationWordsToday.limit_reached ? (
                        <div className={`flex-shrink-0 px-2 py-1 rounded-lg text-xs font-medium ${
                          isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'
                        }`}>
                          ✅
                        </div>
                      ) : (
                        <div className="relative">
                          <button
                            onClick={() => {
                              if (!userToken) {
                                showNotification('Please login to save words', 'info');
                              } else {
                                showNotification(pageLabels.word_bank_locked, 'info');
                              }
                            }}
                            className={`flex-shrink-0 px-2 py-1 rounded-lg transition-all text-xs font-medium ${
                              isDarkMode
                                ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            title={!userToken ? "Login required" : "Pro plan required to save words"}
                          >
                            📚
                          </button>
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center shadow-lg">
                            🔒
                          </span>
                        </div>
                      )}
            </div>
          )}

          {translationResult?.translatedText && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  const latin = displayResult.latinScript || '';
                  const arabic = displayResult.translatedText || '';
                  const text = scriptView === 'latin' ? latin : arabic;
                  copyToClipboard(text);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl transition-all font-semibold text-white shadow-lg hover:shadow-xl transform hover:scale-105 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                {t('copy')}
              </button>

              {/* Only show Play Audio button if audio URL exists and audio is globally enabled */}
              {appSettings.audioGloballyEnabled && translationResult.audioUrl && translationResult.audioUrl.trim() !== '' && (
                <>
                  {hasPaidPlan ? (
                    <button
                      onClick={() => playAudio(translationResult.translatedText, translationResult.audioUrl)}
                      className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm md:text-base ${
                        isDarkMode
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M9 12h6m-3 3v6m0-12V3" />
                      </svg>
                      {t('audio')}
                    </button>
                  ) : (
                    <div className="relative flex-1 sm:flex-initial">
                      <button
                        onClick={() => {
                          if (!userToken) {
                            showNotification('Please login to use audio', 'info');
                          } else {
                            setInitialProfileTab('membership');
                            setCurrentView('profile');
                            showNotification(pageLabels.audio_locked_notification, 'info');
                          }
                        }}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg opacity-40 cursor-not-allowed text-sm md:text-base ${
                          isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                        }`}
                        title={!userToken ? pageLabels.audio_locked_guest : pageLabels.audio_locked_user}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M9 12h6m-3 3v6m0-12V3" />
                        </svg>
                        {t('audio')}
                      </button>
                      <span className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-400 to-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Tablet floating circle buttons removed — bottom sticky bar handles these */}


        {/* Mobile: Alternative translations (disambiguation) */}
        {translationResult?.disambiguation && translationResult?.disambiguationOptions?.length > 0 && (
          <div className={`mt-2 p-3 rounded-xl border ${
            isDarkMode ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-200'
          }`}>
            <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Also means:
            </p>
            <div className="space-y-1.5">
              {translationResult.disambiguationOptions
                .filter(opt => {
                  if (disambiguationSelected) return opt.id !== disambiguationSelected.id;
                  return opt.translatedText !== displayResult.translatedText || opt.note !== (translationResult.note || '');
                })
                .map((option, i) => (
                  <button
                    key={option.id || i}
                    onClick={() => handleDisambiguationSelect(option)}
                    disabled={disambiguationLoading}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all text-sm ${
                      isDarkMode
                        ? 'hover:bg-gray-700 text-gray-300 border border-gray-600 hover:border-blue-500'
                        : 'hover:bg-blue-50 text-gray-700 border border-gray-200 hover:border-blue-400'
                    } ${disambiguationLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className="font-medium">{inputText}</span>
                    <span className={`ml-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>({option.note})</span>
                    <span className={`ml-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>→</span>
                    <span className={`ml-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      {option.latinScript || option.translatedText}
                    </span>
                  </button>
                ))
              }
            </div>
          </div>
        )}

        {/* Mobile: Note card — explains the current word/meaning (HTML from admin) */}
        {displayResult?.note && !translationResult?.disambiguation && (
          <div className={`mt-2 px-4 py-3 rounded-xl border text-sm ${
            isDarkMode ? 'bg-gray-800/40 border-gray-700 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'
          }`}>
            <div className="flex flex-col items-center text-center gap-1">
              <span>ℹ️</span>
              <div className="note-content" style={{ lineHeight: '1.8', wordSpacing: '0.05em' }} dangerouslySetInnerHTML={{ __html: displayResult.note }} />
            </div>
          </div>
        )}

        {/* OLD POPUPS REMOVED - ALL FEATURES NOW USE FULL PAGES */}
        {/* Favorites Popup - REMOVED */}
        {false && isFavoritesOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`relative max-w-2xl w-full max-h-[80vh] overflow-y-auto rounded-lg shadow-xl ${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              {/* Close button */}
              <button
                onClick={() => setIsFavoritesOpen(false)}
                className={`absolute top-3 right-3 p-1 rounded-lg transition-colors z-10 ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Header */}
              <div className="p-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  ❤️ My Favorites
                </h2>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {favorites.length} / {appSettings.maxFavoritesPerUser || 20} saved
                </p>
              </div>

              {/* Favorites List */}
              <div className="p-6">
                {favorites.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      No favorites yet. Tap the ❤️ icon on translations to save them!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {favorites.map((fav, index) => (
                      <div key={fav.id} className={`p-4 rounded-lg border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-start gap-3">
                          {/* Audio Icon - Only show if audio is enabled in backend */}
                          {appSettings.audioGloballyEnabled && (
                            <button
                              onClick={() => playAudio(fav.translatedText, fav.audioUrl)}
                              className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                                isDarkMode
                                  ? 'text-blue-400 hover:bg-gray-600'
                                  : 'text-blue-600 hover:bg-blue-50'
                              }`}
                              title="Play audio"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                              </svg>
                            </button>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                #{index + 1}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                              }`}>
                                {fav.sourceLang.toUpperCase()}
                              </span>
                            </div>
                            
                            <p className={`text-lg font-medium mb-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              "{fav.sourceText}"
                            </p>
                            
                            <p className={`text-base ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                              → {fav.translatedText}
                            </p>
                            
                            <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Added: {new Date(fav.createdAt).toLocaleDateString()} at {new Date(fav.createdAt).toLocaleTimeString()}
                            </p>
                          </div>
                          
                          <button
                            onClick={() => removeFromFavorites(fav.id)}
                            className="flex-shrink-0 p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Remove from favorites"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pro paywall overlay (mobile + tablet) — inside the grid (which is
            position:relative) so the button at top:50%/left:50% lands exactly
            BETWEEN the stacked input and output boxes. */}
        {!hasPaidPlan && (
          <div className="lg:hidden absolute inset-0 z-10 rounded-2xl cursor-not-allowed"
               style={{ backdropFilter: 'blur(0.6px)', WebkitBackdropFilter: 'blur(0.6px)', backgroundColor: isDarkMode ? 'rgba(17,24,39,0.08)' : 'rgba(255,255,255,0.06)' }}>
            <button
              onClick={() => { setInitialProfileTab('membership'); setCurrentView('profile'); }}
              style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
              className="absolute px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold shadow-2xl ring-4 ring-white/30 hover:scale-105 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <span className="text-xl">🔓</span>
              <span>Upgrade to Pro</span>
            </button>
          </div>
        )}

        {/* Mic permission help modal — shown when user previously denied microphone access */}
        {showMicHelpModal && (() => {
          const ua = navigator.userAgent || '';
          let browserKey = 'mic_help_chrome';
          if (/Firefox\//i.test(ua)) browserKey = 'mic_help_firefox';
          else if (/^((?!Chrome|Chromium|Edg|Brave).)*Safari/i.test(ua)) browserKey = 'mic_help_safari';
          return (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.65)' }}
              onClick={() => setShowMicHelpModal(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className={`max-w-md w-full rounded-2xl p-6 shadow-2xl ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}
              >
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-4xl">🎤</span>
                  <div>
                    <h2 className="text-xl font-bold mb-1">{t('mic_help_title')}</h2>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('mic_help_intro')}</p>
                  </div>
                </div>

                <div className={`p-4 rounded-xl mb-3 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                  <p className="text-sm leading-relaxed whitespace-pre-line">{t(browserKey)}</p>
                </div>

                <p className={`text-xs mb-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('mic_help_https_note')}</p>

                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      setShowMicHelpModal(false);
                      // Tiny delay so the modal animates out before we re-prompt
                      setTimeout(() => toggleVoiceInput(), 100);
                    }}
                    className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
                  >
                    {t('mic_help_try_again')}
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className={`flex-1 py-2.5 rounded-xl font-semibold transition-colors ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-100' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  >
                    {t('mic_help_reload')}
                  </button>
                </div>
                <button
                  onClick={() => setShowMicHelpModal(false)}
                  className={`mt-2 w-full py-2 text-sm ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};


export default TranslationInterface;
